import React, { useCallback, useState } from 'react';
import { ModalOverlay, ModalContent } from './styles';
import { isEmpty } from '../../../helpers/common';
import DeleteModal from '../deleteModal';
import { rootStore } from '../../../mobx/rootStore';
import { createPortal } from 'react-dom';

/**
 * Props for the modal component.
 *
 * @property {React.ReactNode} [children] Content to be rendered inside the modal.
 * @property {boolean} [open] Controls whether the modal is open.
 * @property {() => void} [onClose] Callback invoked when the modal requests to be closed.
 * @property {boolean} [isCreateTask] Indicates the modal is used for creating a task.
 * @property {boolean} [isResponsiveMaxWidth] If true, modal uses responsive max-width behavior.
 * @property {string} [maxWidth] CSS `max-width` value for the modal (e.g. "600px", "80%").
 * @property {('auto' | 'visible' | 'hidden' | ' scroll')} [overflow] CSS `overflow` behavior for the modal content.
 */

interface Props {
  children?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
  isCreateTask?: boolean;
  isResponsiveMaxWidth?: boolean;
  maxWidth?: string;
  overflow?: 'auto' | 'visible' | 'hidden' | ' scroll';
}

const Modal = ({ children, open, onClose, isCreateTask, maxWidth, isResponsiveMaxWidth, overflow = 'auto' }: Props) => {
  //States
  const [closeModel, setCloseModel] = useState(false);
  //Mobx state variables
  const { taskStore } = rootStore;
  const { createTaskInput } = taskStore;

  const isClose = () => {
    setCloseModel(false);
  };

  const close = useCallback(() => {
    if (isCreateTask) {
      if (
        !isEmpty(createTaskInput?.name) ||
        !isEmpty(createTaskInput?.description) ||
        !isEmpty(createTaskInput.files)
      ) {
        setCloseModel(true);
      } else {
        rootStore.taskStore.createTaskInput.clear();
        onClose?.();
      }
    } else {
      onClose?.();
    }
  }, [createTaskInput.files, createTaskInput?.description, createTaskInput?.name, isCreateTask, onClose]);

  const onOnkDeleteModel = () => {
    setCloseModel(false);
    rootStore.taskStore.createTaskInput.clear();
    onClose?.();
  };
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      close();
    }
  };
  return (
    <>
      {open &&
        createPortal(
          <>
            <ModalOverlay isOpen={open} onMouseDown={handleOverlayClick}>
              <ModalContent
                role='dialog'
                aria-modal='true'
                aria-labelledby='modal-title'
                maxWidth={maxWidth}
                isResponsiveMaxWidth={isResponsiveMaxWidth || isCreateTask}
                onClick={(e) => e.stopPropagation()}
                overflow={overflow}>
                {children}
              </ModalContent>
            </ModalOverlay>
            <Modal open={closeModel} onClose={isClose} maxWidth={'334px'}>
              <DeleteModal
                isYes={true}
                onClose={isClose}
                onOk={onOnkDeleteModel}
                modalTitle='Confirmation Model'
                description='Are you sure you want close this model?'
              />
            </Modal>
          </>,
          document.body // This ensures the modal is rendered at the root level
        )}
    </>
  );
};

export default Modal;
